export const COMPANY_TIME_ZONE = 'Europe/Madrid';

type DateValue = string | number | Date | null | undefined;

/** Platform timestamps are UTC instants; legacy Timestream values may omit `Z`. */
export function parsePlatformTimestamp(value: DateValue): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return Number.NaN;

  const timestamp = value.trim();
  const missingTimezone = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(timestamp);
  return Date.parse(missingTimezone ? `${timestamp.replace(' ', 'T')}Z` : timestamp);
}

function parts(value: DateValue, options: Intl.DateTimeFormatOptions): Record<string, string> | null {
  const timestamp = parsePlatformTimestamp(value);
  if (Number.isNaN(timestamp)) return null;
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-GB', { timeZone: COMPANY_TIME_ZONE, ...options })
    .formatToParts(date)
    .reduce<Record<string, string>>((result, part) => ({ ...result, [part.type]: part.value }), {});
}

export function formatCompanyDateTime(value: DateValue): string {
  const valueParts = parts(value, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  });
  return valueParts
    ? `${valueParts['day']}/${valueParts['month']}/${valueParts['year']}, ${valueParts['hour']}:${valueParts['minute']}:${valueParts['second']}`
    : '—';
}

export function formatCompanyDate(value: DateValue): string {
  const valueParts = parts(value, { day: '2-digit', month: '2-digit', year: 'numeric' });
  return valueParts ? `${valueParts['day']}/${valueParts['month']}/${valueParts['year']}` : '—';
}

export function formatCompanyTime(value: DateValue): string {
  const valueParts = parts(value, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return valueParts ? `${valueParts['hour']}:${valueParts['minute']}` : '—';
}
